import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, Clock, CheckCircle, Phone, Search, Trash2, ChevronDown,
  ArrowLeft, Mail, MessageSquare, Home, MapPin, Calendar, FileText,
  XCircle, RefreshCw, LogOut, PenSquare, Eye, EyeOff, Plus, Image, Building2, Settings,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRIMO_LOGO = "https://customer-assets.emergentagent.com/job_c18fba98-66cc-4e36-b9db-e24324823938/artifacts/t6fqodcb_BRIMO_Logo_Schwarz.png";

const STATUS_MAP = {
  neu: { label: "Neu", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  kontaktiert: { label: "Kontaktiert", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  abgeschlossen: { label: "Abgeschlossen", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  archiviert: { label: "Archiviert", color: "bg-gray-500/20 text-gray-500 border-gray-500/30" },
};

const TYPE_LABELS = {
  seller: "Verkäufer",
  buyer: "Käufer",
  market_value: "Marktwert",
  contact: "Kontakt",
};

const autoSlug = (t) => t.toLowerCase().replace(/[äöüß]/g, (m) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[m])).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { password });
      onLogin(password);
    } catch {
      setError("Falsches Passwort");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
      <div className="w-full max-w-sm" data-testid="admin-login">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={BRIMO_LOGO} alt="BRIMO" className="h-14 w-auto" data-testid="admin-login-logo" />
          </div>
          <p className="text-gray-500 text-xs uppercase tracking-[0.3em]">Admin Dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passwort eingeben" className="w-full bg-[#1a1a1a] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] transition-colors" data-testid="admin-password-input" autoFocus />
          {error && <p className="text-[#E61D38] text-xs">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-[#E61D38] text-white py-3 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] disabled:bg-gray-700 transition-colors" data-testid="admin-login-btn">{loading ? "..." : "Anmelden"}</button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#1a1a1a] border border-white/5 p-5" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon size={16} className={accent ? "text-[#E61D38]" : "text-gray-500"} />
        <span className="text-2xl font-black text-white" style={{ fontFamily: "Montserrat, sans-serif" }}>{value}</span>
      </div>
      <p className="text-gray-500 text-[10px] uppercase tracking-[0.2em]">{label}</p>
    </div>
  );
}

function LeadDetail({ lead, onClose, onUpdate, onDelete }) {
  const [status, setStatus] = useState(lead.status || "neu");
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await axios.patch(`${API}/leads/${lead.id}`, { status, notes }); onUpdate(); } catch {} finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Lead wirklich löschen?")) return;
    try { await axios.delete(`${API}/leads/${lead.id}`); onDelete(); } catch {}
  };

  const date = new Date(lead.timestamp);
  const formatted = `${date.toLocaleDateString("de-DE")} um ${date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;

  return (
    <div className="bg-[#1a1a1a] border border-white/5 p-6 md:p-8" data-testid="lead-detail">
      <div className="flex items-center justify-between mb-8">
        <button onClick={onClose} className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider hover:text-white transition-colors" data-testid="lead-detail-back"><ArrowLeft size={14} /> Zurück</button>
        <button onClick={remove} className="flex items-center gap-1.5 text-gray-500 text-xs hover:text-[#E61D38] transition-colors" data-testid="lead-delete-btn"><Trash2 size={13} /> Löschen</button>
      </div>
      <div className="mb-6">
        <h2 className="text-white font-bold text-xl mb-1">{lead.name}</h2>
        <p className="text-gray-500 text-xs">{formatted} — {TYPE_LABELS[lead.lead_type] || lead.lead_type}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3 text-gray-300 text-sm hover:border-[#E61D38]/30 transition-colors"><Mail size={14} className="text-[#E61D38]" /> {lead.email}</a>}
        {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3 text-gray-300 text-sm hover:border-[#E61D38]/30 transition-colors"><Phone size={14} className="text-[#E61D38]" /> {lead.phone}</a>}
        {lead.property_type && <div className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3 text-gray-400 text-sm"><Home size={14} className="text-gray-600" /> {lead.property_type}</div>}
        {lead.district && <div className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3 text-gray-400 text-sm"><MapPin size={14} className="text-gray-600" /> {lead.district}</div>}
        {lead.budget && <div className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3 text-gray-400 text-sm"><FileText size={14} className="text-gray-600" /> {lead.budget}</div>}
        {lead.page_source && <div className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 px-4 py-3 text-gray-400 text-sm"><Calendar size={14} className="text-gray-600" /> {lead.page_source}</div>}
      </div>
      {lead.message && <div className="mb-8"><p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Nachricht</p><div className="bg-[#0d0d0d] border border-white/5 p-4 text-gray-300 text-sm leading-relaxed">{lead.message}</div></div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Status</p>
          <div className="relative">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] appearance-none cursor-pointer" data-testid="lead-status-select">
              {Object.entries(STATUS_MAP).map(([key, { label }]) => (<option key={key} value={key}>{label}</option>))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Notizen</p>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={1} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] resize-none" placeholder="Interne Notizen..." data-testid="lead-notes-input" />
        </div>
      </div>
      <button onClick={save} disabled={saving} className="bg-[#E61D38] text-white px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] disabled:bg-gray-700 transition-colors" data-testid="lead-save-btn">{saving ? "Speichern..." : "Speichern"}</button>
    </div>
  );
}

/* ─── Blog / Aktuelles Editor ─── */
function BlogEditor({ post, onSave, onCancel }) {
  const [title, setTitle] = useState(post?.title || "");
  const [slug, setSlug] = useState(post?.slug || "");
  const [excerpt, setExcerpt] = useState(post?.excerpt || "");
  const [content, setContent] = useState(post?.content || "");
  const [imageUrl, setImageUrl] = useState(post?.image_url || "");
  const [category, setCategory] = useState(post?.category || "Allgemein");
  const [author, setAuthor] = useState(post?.author || "Janine Muth");
  const [published, setPublished] = useState(post?.published !== false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleTitleChange = (v) => { setTitle(v); if (!post) setSlug(autoSlug(v)); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try { const res = await axios.post(`${API}/blog/upload-image`, { image: reader.result, filename: file.name }); setImageUrl(res.data.url); } catch {} finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!title || !slug || !content) return;
    setSaving(true);
    try {
      if (post?.id) { await axios.patch(`${API}/blog/${post.id}`, { title, slug, excerpt, content, image_url: imageUrl, category, author, published }); }
      else { await axios.post(`${API}/blog`, { title, slug, excerpt, content, image_url: imageUrl, category, author }); }
      onSave();
    } catch (err) { alert(err.response?.data?.detail || "Fehler beim Speichern"); } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#1a1a1a] border border-white/5 p-6 md:p-8" data-testid="blog-editor">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">{post ? "Beitrag bearbeiten" : "Neuer Beitrag"}</h3>
        <button onClick={onCancel} className="text-gray-500 text-xs hover:text-white transition-colors" data-testid="blog-editor-cancel">Abbrechen</button>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Titel</label><input value={title} onChange={(e) => handleTitleChange(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="blog-title-input" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Slug (URL)</label><input value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-gray-400 px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="blog-slug-input" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Kategorie</label><input value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Autor</label><input value={author} onChange={(e) => setAuthor(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" /></div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Beitragsbild</label>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 bg-[#0d0d0d] border border-white/10 text-gray-400 px-4 py-3 text-xs cursor-pointer hover:border-[#E61D38]/30 transition-colors"><Image size={14} /> {uploading ? "Hochladen..." : "Bild auswählen"}<input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="blog-image-upload" /></label>
            {imageUrl && <img src={imageUrl} alt="Vorschau" className="h-12 w-20 object-cover border border-white/10" />}
            {imageUrl && <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="flex-1 bg-[#0d0d0d] border border-white/10 text-gray-400 px-3 py-2 text-xs focus:outline-none" placeholder="Oder URL eingeben" />}
          </div>
        </div>
        <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Kurzfassung</label><textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] resize-none" data-testid="blog-excerpt-input" /></div>
        <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Inhalt (HTML)</label><textarea value={content} onChange={(e) => setContent(e.target.value)} rows={12} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] resize-y font-mono" data-testid="blog-content-input" /></div>
        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-gray-400 text-xs cursor-pointer"><input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-[#E61D38]" />{published ? <><Eye size={12} /> Veröffentlicht</> : <><EyeOff size={12} /> Entwurf</>}</label>
          <button onClick={handleSave} disabled={saving || !title || !slug || !content} className="bg-[#E61D38] text-white px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] disabled:bg-gray-700 transition-colors" data-testid="blog-save-btn">{saving ? "Speichern..." : post ? "Aktualisieren" : "Veröffentlichen"}</button>
        </div>
      </div>
    </div>
  );
}

function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchPosts = useCallback(async () => { setLoading(true); try { const res = await axios.get(`${API}/blog`, { params: { published_only: false } }); setPosts(res.data); } catch {} finally { setLoading(false); } }, []);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleDelete = async (id) => { if (!window.confirm("Beitrag wirklich löschen?")) return; await axios.delete(`${API}/blog/${id}`); fetchPosts(); };
  const togglePublish = async (post) => { await axios.patch(`${API}/blog/${post.id}`, { published: !post.published }); fetchPosts(); };

  if (editing !== null) return <BlogEditor post={editing === "new" ? null : editing} onSave={() => { setEditing(null); fetchPosts(); }} onCancel={() => setEditing(null)} />;

  return (
    <div data-testid="blog-admin">
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 text-xs">{posts.length} Beiträge</p>
        <button onClick={() => setEditing("new")} className="flex items-center gap-2 bg-[#E61D38] text-white px-5 py-2.5 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] transition-colors" data-testid="blog-new-btn"><Plus size={14} /> Neuer Beitrag</button>
      </div>
      {loading ? <p className="text-gray-500 text-sm">Laden...</p> : posts.length === 0 ? (
        <div className="text-center py-16" data-testid="blog-admin-empty"><PenSquare size={32} className="text-gray-700 mx-auto mb-3" /><p className="text-gray-500 text-sm">Noch keine Beiträge erstellt</p></div>
      ) : (
        <div className="space-y-2">
          {posts.map((post) => (
            <div key={post.id} className="bg-[#1a1a1a] border border-white/5 p-4 flex items-center gap-4 hover:border-white/10 transition-colors" data-testid={`blog-admin-row-${post.slug}`}>
              {post.image_url && <img src={post.image_url} alt="" className="w-16 h-10 object-cover border border-white/5 flex-shrink-0" />}
              <div className="flex-1 min-w-0"><p className="text-white text-sm font-bold truncate">{post.title}</p><p className="text-gray-600 text-[10px]">{new Date(post.created_at).toLocaleDateString("de-DE")} — {post.author}</p></div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => togglePublish(post)} className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${post.published ? "text-emerald-400 border-emerald-500/30" : "text-gray-500 border-gray-500/30"}`} data-testid={`blog-toggle-${post.slug}`}>{post.published ? "Live" : "Entwurf"}</button>
                <button onClick={() => setEditing(post)} className="text-gray-500 hover:text-white transition-colors p-1" data-testid={`blog-edit-${post.slug}`}><PenSquare size={14} /></button>
                <button onClick={() => handleDelete(post.id)} className="text-gray-500 hover:text-[#E61D38] transition-colors p-1" data-testid={`blog-delete-${post.slug}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Objekte Editor ─── */
function ObjekteEditor({ objekt, onSave, onCancel }) {
  const [title, setTitle] = useState(objekt?.title || "");
  const [slug, setSlug] = useState(objekt?.slug || "");
  const [description, setDescription] = useState(objekt?.description || "");
  const [images, setImages] = useState(objekt?.images || []);
  const [district, setDistrict] = useState(objekt?.district || "");
  const [propertyType, setPropertyType] = useState(objekt?.property_type || "");
  const [livingArea, setLivingArea] = useState(objekt?.living_area || "");
  const [yearBuilt, setYearBuilt] = useState(objekt?.year_built || "");
  const [price, setPrice] = useState(objekt?.price || "");
  const [status, setStatus] = useState(objekt?.status || "zum_verkauf");
  const [published, setPublished] = useState(objekt?.published !== false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleTitleChange = (v) => { setTitle(v); if (!objekt) setSlug(autoSlug(v)); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await axios.post(`${API}/objekte/upload-image`, { image: reader.result, filename: file.name });
        setImages([...images, res.data.url]);
      } catch {} finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (i) => setImages(images.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!title || !slug || !description) return;
    setSaving(true);
    const payload = { title, slug, description, images, district, property_type: propertyType, living_area: livingArea, year_built: yearBuilt, price, status, featured: true };
    try {
      if (objekt?.id) { await axios.patch(`${API}/objekte/${objekt.id}`, { ...payload, published }); }
      else { await axios.post(`${API}/objekte`, payload); }
      onSave();
    } catch (err) { alert(err.response?.data?.detail || "Fehler beim Speichern"); } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#1a1a1a] border border-white/5 p-6 md:p-8" data-testid="objekte-editor">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">{objekt ? "Objekt bearbeiten" : "Neues Objekt"}</h3>
        <button onClick={onCancel} className="text-gray-500 text-xs hover:text-white transition-colors" data-testid="objekte-editor-cancel">Abbrechen</button>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Titel</label><input value={title} onChange={(e) => handleTitleChange(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-title-input" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Slug (URL)</label><input value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-gray-400 px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-slug-input" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Objekttyp</label><input value={propertyType} onChange={(e) => setPropertyType(e.target.value)} placeholder="z.B. Einfamilienhaus" className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-type-input" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Stadt</label><input value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="z.B. Haan" className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-district-input" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Preis</label><input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="z.B. 385.000 EUR" className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-price-input" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Wohnfläche</label><input value={livingArea} onChange={(e) => setLivingArea(e.target.value)} placeholder="z.B. 120 m²" className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-area-input" /></div>
          <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Baujahr</label><input value={yearBuilt} onChange={(e) => setYearBuilt(e.target.value)} placeholder="z.B. 1985" className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38]" data-testid="objekte-year-input" /></div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Status</label>
            <div className="relative">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] appearance-none cursor-pointer" data-testid="objekte-status-select">
                <option value="zum_verkauf">Zum Verkauf</option>
                <option value="verkauft">Verkauft (Referenz)</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Bilder</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {images.map((img, i) => (
              <div key={i} className="relative group">
                <img src={img} alt="" className="h-16 w-24 object-cover border border-white/10" />
                <button onClick={() => removeImage(i)} className="absolute -top-1.5 -right-1.5 bg-[#E61D38] text-white w-5 h-5 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">x</button>
              </div>
            ))}
            <label className="flex items-center gap-2 bg-[#0d0d0d] border border-white/10 border-dashed text-gray-400 h-16 w-24 text-xs cursor-pointer hover:border-[#E61D38]/30 transition-colors justify-center">
              <Plus size={14} />
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="objekte-image-upload" />
            </label>
          </div>
          {uploading && <p className="text-gray-500 text-xs">Bild wird hochgeladen...</p>}
        </div>
        <div><label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1 block">Beschreibung (HTML)</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={10} className="w-full bg-[#0d0d0d] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] resize-y font-mono" data-testid="objekte-description-input" /></div>
        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-gray-400 text-xs cursor-pointer"><input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} className="accent-[#E61D38]" />{published ? <><Eye size={12} /> Veröffentlicht</> : <><EyeOff size={12} /> Entwurf</>}</label>
          <button onClick={handleSave} disabled={saving || !title || !slug || !description} className="bg-[#E61D38] text-white px-6 py-3 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] disabled:bg-gray-700 transition-colors" data-testid="objekte-save-btn">{saving ? "Speichern..." : objekt ? "Aktualisieren" : "Veröffentlichen"}</button>
        </div>
      </div>
    </div>
  );
}

function ObjekteAdmin() {
  const [objekte, setObjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const fetchObjekte = useCallback(async () => { setLoading(true); try { const res = await axios.get(`${API}/objekte`, { params: { published_only: false } }); setObjekte(res.data); } catch {} finally { setLoading(false); } }, []);
  useEffect(() => { fetchObjekte(); }, [fetchObjekte]);

  const handleDelete = async (id) => { if (!window.confirm("Objekt wirklich löschen?")) return; await axios.delete(`${API}/objekte/${id}`); fetchObjekte(); };

  const moveItem = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= objekte.length) return;
    const items = [...objekte];
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    setObjekte(items);
    setSaving(true);
    try { await axios.post(`${API}/objekte/reorder`, { order: items.map((o) => o.id) }); } catch {} finally { setSaving(false); }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetIdx) => {
    if (dragIdx !== null && dragIdx !== targetIdx) moveItem(dragIdx, targetIdx);
    setDragIdx(null);
  };

  if (editing !== null) return <ObjekteEditor objekt={editing === "new" ? null : editing} onSave={() => { setEditing(null); fetchObjekte(); }} onCancel={() => setEditing(null)} />;

  return (
    <div data-testid="objekte-admin">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <p className="text-gray-500 text-xs">{objekte.length} Objekte</p>
          {saving && <span className="text-[#E61D38] text-[10px] uppercase tracking-wider animate-pulse">Sortierung wird gespeichert...</span>}
        </div>
        <button onClick={() => setEditing("new")} className="flex items-center gap-2 bg-[#E61D38] text-white px-5 py-2.5 font-bold uppercase tracking-wider text-xs hover:bg-[#c41930] transition-colors" data-testid="objekte-new-btn"><Plus size={14} /> Neues Objekt</button>
      </div>
      {!loading && objekte.length > 1 && (
        <p className="text-gray-600 text-[10px] mb-3 uppercase tracking-wider">Reihenfolge per Drag &amp; Drop oder Pfeiltasten ändern</p>
      )}
      {loading ? <p className="text-gray-500 text-sm">Laden...</p> : objekte.length === 0 ? (
        <div className="text-center py-16" data-testid="objekte-admin-empty"><Building2 size={32} className="text-gray-700 mx-auto mb-3" /><p className="text-gray-500 text-sm">Noch keine Objekte erstellt</p></div>
      ) : (
        <div className="space-y-2">
          {objekte.map((obj, idx) => (
            <div
              key={obj.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              className={`bg-[#1a1a1a] border p-4 flex items-center gap-3 transition-colors cursor-grab active:cursor-grabbing ${dragIdx === idx ? "border-[#E61D38]/40 bg-[#E61D38]/5" : "border-white/5 hover:border-white/10"}`}
              data-testid={`objekte-admin-row-${obj.slug}`}
            >
              {/* Sort controls */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  onClick={() => moveItem(idx, idx - 1)}
                  disabled={idx === 0}
                  className="text-gray-600 hover:text-white disabled:text-gray-800 transition-colors p-0.5"
                  data-testid={`objekte-up-${obj.slug}`}
                >
                  <ChevronDown size={14} className="rotate-180" />
                </button>
                <span className="text-gray-700 text-[10px] text-center font-mono w-5">{idx + 1}</span>
                <button
                  onClick={() => moveItem(idx, idx + 1)}
                  disabled={idx === objekte.length - 1}
                  className="text-gray-600 hover:text-white disabled:text-gray-800 transition-colors p-0.5"
                  data-testid={`objekte-down-${obj.slug}`}
                >
                  <ChevronDown size={14} />
                </button>
              </div>
              {obj.images?.[0] && <img src={obj.images[0]} alt="" className="w-16 h-10 object-cover border border-white/5 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{obj.title}</p>
                <p className="text-gray-600 text-[10px]">{obj.property_type} — {obj.district || "Kreis Mettmann"} — {obj.price || "Preis auf Anfrage"}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${obj.status === "verkauft" ? "text-emerald-400 border-emerald-500/30" : "text-blue-400 border-blue-500/30"}`}>{obj.status === "verkauft" ? "Verkauft" : "Aktiv"}</span>
                <button onClick={() => setEditing(obj)} className="text-gray-500 hover:text-white transition-colors p-1" data-testid={`objekte-edit-${obj.slug}`}><PenSquare size={14} /></button>
                <button onClick={() => handleDelete(obj.id)} className="text-gray-500 hover:text-[#E61D38] transition-colors p-1" data-testid={`objekte-delete-${obj.slug}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Site Settings ─── */
function ToggleSwitch({ enabled, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? "bg-[#E61D38]" : "bg-gray-700"}`}
      data-testid={testId}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? "translate-x-5" : ""}`} />
    </button>
  );
}

function SiteSettings() {
  const [settings, setSettings] = useState({ aktuelles_enabled: true, objekte_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).finally(() => setLoading(false));
  }, []);

  const toggle = async (key) => {
    const newVal = !settings[key];
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/settings`, { [key]: newVal });
      setSettings(res.data);
    } catch {} finally { setSaving(false); }
  };

  if (loading) return <p className="text-gray-500 text-sm">Laden...</p>;

  return (
    <div data-testid="site-settings">
      <div className="flex items-center gap-2 mb-8">
        <Settings size={16} className="text-[#E61D38]" />
        <h2 className="text-white font-bold text-sm uppercase tracking-wider">Website-Einstellungen</h2>
      </div>

      <div className="space-y-4 max-w-lg">
        <div className="bg-[#1a1a1a] border border-white/5 p-5 flex items-center justify-between" data-testid="setting-objekte">
          <div>
            <p className="text-white text-sm font-bold mb-0.5">Objekte & Referenzen</p>
            <p className="text-gray-500 text-xs">Seite, Navigation und Homepage-Bereich für Objekte anzeigen</p>
          </div>
          <ToggleSwitch
            enabled={settings.objekte_enabled}
            onChange={() => toggle("objekte_enabled")}
            testId="toggle-objekte"
          />
        </div>

        <div className="bg-[#1a1a1a] border border-white/5 p-5 flex items-center justify-between" data-testid="setting-aktuelles">
          <div>
            <p className="text-white text-sm font-bold mb-0.5">Aktuelles & Neuigkeiten</p>
            <p className="text-gray-500 text-xs">Blog-Seite und Navigation für Aktuelles anzeigen</p>
          </div>
          <ToggleSwitch
            enabled={settings.aktuelles_enabled}
            onChange={() => toggle("aktuelles_enabled")}
            testId="toggle-aktuelles"
          />
        </div>
      </div>

      {saving && <p className="text-gray-500 text-xs mt-4">Speichern...</p>}

      <div className="mt-8 p-4 bg-[#1a1a1a]/50 border border-white/5">
        <p className="text-gray-500 text-xs leading-relaxed">
          Deaktivierte Bereiche werden sofort von der Website ausgeblendet: Navigation, Seiten und Homepage-Elemente verschwinden.
          Die Inhalte bleiben gespeichert und können jederzeit wieder aktiviert werden.
        </p>
      </div>
    </div>
  );
}

/* ─── Leads Admin ─── */
function LeadsAdmin({ leads, stats, loading, filterStatus, setFilterStatus, filterType, setFilterType, search, setSearch, selected, setSelected, fetchData }) {
  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="stats-grid">
          <StatCard icon={Users} label="Gesamt" value={stats.total} accent />
          <StatCard icon={Clock} label="Neu" value={stats.neu} />
          <StatCard icon={Phone} label="Kontaktiert" value={stats.kontaktiert} />
          <StatCard icon={CheckCircle} label="Abgeschlossen" value={stats.abgeschlossen} />
        </div>
      )}
      {selected ? (
        <LeadDetail lead={selected} onClose={() => { setSelected(null); fetchData(); }} onUpdate={() => { setSelected(null); fetchData(); }} onDelete={() => { setSelected(null); fetchData(); }} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="filters">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, E-Mail oder Telefon..." className="w-full bg-[#1a1a1a] border border-white/10 text-white pl-9 pr-4 py-2.5 text-xs focus:outline-none focus:border-[#E61D38] transition-colors" data-testid="search-input" />
            </div>
            <div className="relative">
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#1a1a1a] border border-white/10 text-gray-300 text-xs px-4 py-2.5 pr-8 appearance-none cursor-pointer focus:outline-none focus:border-[#E61D38]" data-testid="filter-status">
                <option value="">Alle Status</option>
                {Object.entries(STATUS_MAP).map(([key, { label }]) => (<option key={key} value={key}>{label}</option>))}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
            <div className="relative">
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-[#1a1a1a] border border-white/10 text-gray-300 text-xs px-4 py-2.5 pr-8 appearance-none cursor-pointer focus:outline-none focus:border-[#E61D38]" data-testid="filter-type">
                <option value="">Alle Typen</option>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (<option key={key} value={key}>{label}</option>))}
              </select>
              <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
            {(filterStatus || filterType || search) && <button onClick={() => { setFilterStatus(""); setFilterType(""); setSearch(""); }} className="flex items-center gap-1.5 text-gray-500 text-xs hover:text-[#E61D38] transition-colors" data-testid="clear-filters"><XCircle size={12} /> Filter zurücksetzen</button>}
          </div>
          <div className="bg-[#1a1a1a] border border-white/5 overflow-hidden" data-testid="leads-table">
            {loading ? <div className="p-12 text-center text-gray-500 text-sm">Laden...</div> : leads.length === 0 ? (
              <div className="p-12 text-center" data-testid="no-leads"><MessageSquare size={32} className="text-gray-700 mx-auto mb-3" /><p className="text-gray-500 text-sm">Keine Leads gefunden</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500 px-5 py-3 font-medium">Name</th>
                    <th className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500 px-5 py-3 font-medium hidden md:table-cell">Kontakt</th>
                    <th className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500 px-5 py-3 font-medium">Typ</th>
                    <th className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500 px-5 py-3 font-medium">Status</th>
                    <th className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500 px-5 py-3 font-medium hidden lg:table-cell">Datum</th>
                  </tr></thead>
                  <tbody>
                    {leads.map((lead) => { const st = STATUS_MAP[lead.status] || STATUS_MAP.neu; const d = new Date(lead.timestamp); return (
                      <tr key={lead.id} onClick={() => setSelected(lead)} className="border-b border-white/3 hover:bg-white/[0.02] cursor-pointer transition-colors" data-testid={`lead-row-${lead.id}`}>
                        <td className="px-5 py-4"><p className="text-white text-sm font-medium">{lead.name}</p><p className="text-gray-600 text-xs mt-0.5 md:hidden">{lead.email}</p></td>
                        <td className="px-5 py-4 hidden md:table-cell"><p className="text-gray-400 text-xs">{lead.email}</p>{lead.phone && <p className="text-gray-600 text-xs mt-0.5">{lead.phone}</p>}</td>
                        <td className="px-5 py-4"><span className="text-gray-400 text-xs">{TYPE_LABELS[lead.lead_type] || lead.lead_type}</span></td>
                        <td className="px-5 py-4"><span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${st.color}`}>{st.label}</span></td>
                        <td className="px-5 py-4 hidden lg:table-cell"><span className="text-gray-600 text-xs">{d.toLocaleDateString("de-DE")}</span></td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-gray-600 text-xs mt-3">{leads.length} {leads.length === 1 ? "Lead" : "Leads"}</p>
        </>
      )}
    </>
  );
}

/* ─── Main Admin Page ─── */
export default function AdminPage() {
  const [auth, setAuth] = useState(sessionStorage.getItem("brimo_admin") || "");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.lead_type = filterType;
      if (search) params.search = search;
      const [leadsRes, statsRes] = await Promise.all([axios.get(`${API}/leads`, { params }), axios.get(`${API}/leads/stats`)]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filterStatus, filterType, search]);

  useEffect(() => { if (auth) fetchData(); }, [auth, fetchData]);

  const handleLogin = (pw) => { sessionStorage.setItem("brimo_admin", pw); setAuth(pw); };
  const handleLogout = () => { sessionStorage.removeItem("brimo_admin"); setAuth(""); };

  if (!auth) return <LoginScreen onLogin={handleLogin} />;

  const tabs = [
    { key: "leads", label: "Leads", icon: Users },
    { key: "objekte", label: "Objekte", icon: Building2 },
    { key: "aktuelles", label: "Aktuelles", icon: PenSquare },
  ];

  return (
    <div className="min-h-screen bg-[#0d0d0d]" data-testid="admin-dashboard">
      <div className="border-b border-white/5 bg-[#0d0d0d] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRIMO_LOGO} alt="BRIMO" className="h-8 w-auto" data-testid="admin-brimo-logo" />
            <span className="text-gray-500 text-[10px] uppercase tracking-[0.3em]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchData} className="text-gray-500 hover:text-white transition-colors" data-testid="refresh-btn"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
            <a href="/" className="text-gray-500 text-xs hover:text-white transition-colors">Website</a>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-gray-500 text-xs hover:text-[#E61D38] transition-colors" data-testid="logout-btn"><LogOut size={12} /> Abmelden</button>
          </div>
        </div>
      </div>

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          <div className="flex gap-0">
            {tabs.map(({ key, label, icon: TabIcon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-5 py-3.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors border-b-2 ${tab === key ? "text-[#E61D38] border-[#E61D38]" : "text-gray-500 border-transparent hover:text-gray-300"}`}
                data-testid={`tab-${key}`}
              >
                <span className="flex items-center gap-2"><TabIcon size={13} /> {label}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setTab("settings")}
            className={`px-4 py-3.5 text-xs transition-colors border-b-2 ${tab === "settings" ? "text-[#E61D38] border-[#E61D38]" : "text-gray-500 border-transparent hover:text-gray-300"}`}
            data-testid="tab-settings"
          >
            <Settings size={15} />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {tab === "leads" && <LeadsAdmin leads={leads} stats={stats} loading={loading} filterStatus={filterStatus} setFilterStatus={setFilterStatus} filterType={filterType} setFilterType={setFilterType} search={search} setSearch={setSearch} selected={selected} setSelected={setSelected} fetchData={fetchData} />}
        {tab === "objekte" && <ObjekteAdmin />}
        {tab === "aktuelles" && <BlogAdmin />}
        {tab === "settings" && <SiteSettings />}
      </div>
    </div>
  );
}
